import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Animated,
  Easing,
  Platform,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import Colors from "../../../../Resources/Color";
import { useDevice } from "../../../_layout";
import { toRgba } from "../../../utils/colorUtils";
import Tooltip from "../../../Components/Tooltip";
import HoverableTouchableOpacity from "../../../Components/HoverableTouchableOpacity";
import { buildSummaryBlocks } from "./blocksListUtils";
import BlockSummaryOverviewContent from "./BlockSummaryOverviewContent";
import BlockCard from "../../../Block/shared/BlockCard";
import {
  resolveSummaryRowEdgeHoverLock,
  resolveSummaryRowScrollIndicators,
} from "./blockSummaryRowScrollUtils";

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(15, 15, 15, 0.35)",
  },
  backdropPressable: {
    ...StyleSheet.absoluteFillObject,
  },
  modalSurface: {
    width: "100%",
    maxWidth: 1360,
    maxHeight: "94%",
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 22,
    elevation: 10,
    overflow: "hidden",
  },
  header: {
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderColor,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    fontSize: 18,
    lineHeight: 24,
    color: Colors.blackColor,
    fontWeight: "600",
    flexShrink: 1,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  closeButtonHovered: {
    backgroundColor: Colors.hoveredGray,
  },
  contentScroll: {
    flex: 1,
    minHeight: 220,
  },
  contentScrollBody: {
    paddingHorizontal: 12,
    paddingTop: 12,
    paddingBottom: 14,
  },
  bottomRowWrap: {
    borderTopWidth: 1,
    borderTopColor: Colors.borderColor,
    backgroundColor: Colors.lightGrayColor,
    paddingTop: 10,
    paddingBottom: 12,
    position: "relative",
  },
  bottomRowContent: {
    paddingHorizontal: 16,
    gap: 10,
    alignItems: "stretch",
  },
  rowCardWrapper: {
    width: 260,
    minWidth: 260,
    maxWidth: 260,
    flexShrink: 0,
    flexGrow: 0,
  },
  rowScrollOverlay: {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 2,
  },
  edgeFade: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 16,
  },
  edgeFadeWide: {
    width: 36,
  },
  edgeFadeLeft: {
    left: 0,
  },
  edgeFadeRight: {
    right: 0,
  },
  edgeBlocker: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 16,
  },
  edgeBlockerLeft: {
    left: 0,
  },
  edgeBlockerRight: {
    right: 0,
  },
  scrollButton: {
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    backgroundColor: "transparent",
    opacity: 0.4,
  },
  scrollButtonLeft: {
    left: 0,
    alignItems: "flex-start",
  },
  scrollButtonRight: {
    right: 0,
    alignItems: "flex-end",
  },
});

const ROW_SCROLL_STEP = 270;

const BlockSummaryModal = observer(
  ({
    isVisible,
    onClose,
    initialBlockId,
    blocks,
    screenSize,
    activeBlockId = null,
  }) => {
    const { t, i18n } = useTranslation();
    const { isMobile } = useDevice();

    const [closeHovered, setCloseHovered] = useState(false);
    const [mounted, setMounted] = useState(isVisible);
    const [selectedBlockId, setSelectedBlockId] = useState(
      initialBlockId ?? null,
    );
    const [isHoveringSummaryRow, setIsHoveringSummaryRow] = useState(false);
    const [hoveredScrollButton, setHoveredScrollButton] = useState(null);
    const [rowEdgeHoverLock, setRowEdgeHoverLock] = useState({
      left: false,
      right: false,
    });
    const [rowScrollIndicators, setRowScrollIndicators] = useState({
      canScrollLeft: false,
      canScrollRight: false,
    });

    const progress = useRef(new Animated.Value(0)).current;
    const rowScrollRef = useRef(null);
    const rowViewportWidthRef = useRef(0);
    const rowContentWidthRef = useRef(0);
    const rowScrollXRef = useRef(0);
    const rowLayoutsRef = useRef({});
    const ensureVisibleTimerRef = useRef(null);
    const hoveredScrollButtonRef = useRef(null);
    const rowEdgeHoverLockRef = useRef({
      left: false,
      right: false,
    });
    const rowScrollIndicatorsRef = useRef({
      canScrollLeft: false,
      canScrollRight: false,
    });

    const fadeColors = useMemo(() => {
      return {
        solid: toRgba(Colors.backgroundColor, 0.96),
        transparent: toRgba(Colors.backgroundColor, 0),
      };
    }, []);

    const blocksSortKey = Array.isArray(blocks)
      ? blocks
          .map((block) => {
            const parsedDate =
              block?.initialDate instanceof Date
                ? block.initialDate.getTime()
                : new Date(block?.initialDate).getTime();
            return `${block?.id ?? ""}:${Number.isFinite(parsedDate) ? parsedDate : "invalid"}:${block?.protocolsAndExercises?.length ?? 0}`;
          })
          .join("|")
      : "";

    const summaryBlocks = useMemo(
      () => buildSummaryBlocks(blocks),
      [blocksSortKey],
    );

    const selectedBlock = useMemo(
      () =>
        summaryBlocks.find((block) => block?.id === selectedBlockId) ??
        summaryBlocks[0] ??
        null,
      [summaryBlocks, selectedBlockId],
    );

    const setRowEdgeHoverLockState = useCallback((nextLock) => {
      rowEdgeHoverLockRef.current = nextLock;
      setRowEdgeHoverLock(nextLock);
    }, []);

    useEffect(() => {
      hoveredScrollButtonRef.current = hoveredScrollButton;
    }, [hoveredScrollButton]);

    const clearRowEdgeHoverLock = useCallback(
      (side) => {
        const currentLock = rowEdgeHoverLockRef.current;
        if (!currentLock[side]) return;
        setRowEdgeHoverLockState({ ...currentLock, [side]: false });
      },
      [setRowEdgeHoverLockState],
    );

    const updateRowScrollIndicators = useCallback(
      (nextScrollLeft, nextContainerWidth, nextContentWidth) => {
        const scrollLeft =
          typeof nextScrollLeft === "number"
            ? nextScrollLeft
            : rowScrollXRef.current;
        const containerWidth =
          typeof nextContainerWidth === "number"
            ? nextContainerWidth
            : rowViewportWidthRef.current;
        const contentWidth =
          typeof nextContentWidth === "number"
            ? nextContentWidth
            : rowContentWidthRef.current;

        rowScrollXRef.current = scrollLeft;
        rowViewportWidthRef.current = containerWidth;
        rowContentWidthRef.current = contentWidth;

        const { canScrollLeft, canScrollRight } =
          resolveSummaryRowScrollIndicators({
            scrollLeft,
            containerWidth,
            contentWidth,
          });

        const prev = rowScrollIndicatorsRef.current;
        if (
          prev.canScrollLeft !== canScrollLeft ||
          prev.canScrollRight !== canScrollRight
        ) {
          const next = { canScrollLeft, canScrollRight };
          rowScrollIndicatorsRef.current = next;
          setRowScrollIndicators(next);
        }

        const prevLock = rowEdgeHoverLockRef.current;
        const nextLock = resolveSummaryRowEdgeHoverLock({
          previousLock: prevLock,
          hoveredScrollButton: hoveredScrollButtonRef.current,
          canScrollLeft,
          canScrollRight,
        });

        if (
          prevLock.left !== nextLock.left ||
          prevLock.right !== nextLock.right
        ) {
          setRowEdgeHoverLockState(nextLock);
        }
      },
      [setRowEdgeHoverLockState],
    );

    const nudgeRowScroll = useCallback(
      (direction) => {
        const containerWidth = rowViewportWidthRef.current;
        const contentWidth = rowContentWidthRef.current;
        const maxScrollLeft = Math.max(0, contentWidth - containerWidth);
        const current = rowScrollXRef.current;
        const next = Math.min(
          maxScrollLeft,
          Math.max(0, current + direction * ROW_SCROLL_STEP),
        );

        rowScrollRef.current?.scrollTo({ x: next, y: 0, animated: true });
        updateRowScrollIndicators(next, containerWidth, contentWidth);
      },
      [updateRowScrollIndicators],
    );

    const ensureSelectedIsVisible = useCallback(
      (animated = true) => {
        if (screenSize === "small") return false;
        if (!selectedBlock) return false;
        const layout = rowLayoutsRef.current[selectedBlock.id];
        if (!layout) return false;

        const viewportWidth = rowViewportWidthRef.current;
        if (!viewportWidth || viewportWidth <= 0) return false;

        const currentX = rowScrollXRef.current;
        const leftVisibleEdge = currentX;
        const rightVisibleEdge = currentX + viewportWidth;
        const safetyPadding = 12;

        let targetX = null;

        if (layout.x < leftVisibleEdge + safetyPadding) {
          targetX = Math.max(layout.x - safetyPadding, 0);
        } else if (
          layout.x + layout.width >
          rightVisibleEdge - safetyPadding
        ) {
          targetX = Math.max(
            layout.x + layout.width - viewportWidth + safetyPadding,
            0,
          );
        }

        if (targetX !== null) {
          rowScrollRef.current?.scrollTo({ x: targetX, y: 0, animated });
          updateRowScrollIndicators(
            targetX,
            rowViewportWidthRef.current,
            rowContentWidthRef.current,
          );
        }
        return true;
      },
      [screenSize, selectedBlock, updateRowScrollIndicators],
    );

    useEffect(() => {
      if (isVisible) {
        setMounted(true);
        requestAnimationFrame(() => {
          Animated.timing(progress, {
            toValue: 1,
            duration: 210,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
          }).start();
        });
        return;
      }

      if (!mounted) return;

      Animated.timing(progress, {
        toValue: 0,
        duration: 160,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished) {
          setMounted(false);
        }
      });
    }, [isVisible, mounted, progress]);

    useEffect(() => {
      if (!isVisible) return;

      if (summaryBlocks.length === 0) {
        setSelectedBlockId(null);
        return;
      }

      const preferredBlock =
        summaryBlocks.find((block) => block?.id === initialBlockId) ??
        summaryBlocks[0];
      setSelectedBlockId(preferredBlock?.id ?? null);
    }, [isVisible, initialBlockId, summaryBlocks]);

    useEffect(() => {
      if (!isVisible) return;
      if (summaryBlocks.length === 0) return;
      if (selectedBlockId && summaryBlocks.some((b) => b?.id === selectedBlockId)) {
        return;
      }
      setSelectedBlockId(summaryBlocks[0].id);
    }, [isVisible, selectedBlockId, summaryBlocks]);

    useEffect(() => {
      rowLayoutsRef.current = {};
    }, [blocksSortKey]);

    useEffect(() => {
      if (!isVisible || screenSize === "small") return;
      if (!selectedBlock) return;
      let cancelled = false;
      let attempts = 0;
      const maxAttempts = 10;

      if (ensureVisibleTimerRef.current) {
        clearTimeout(ensureVisibleTimerRef.current);
      }

      const tryAlignSelected = () => {
        if (cancelled) return;
        const aligned = ensureSelectedIsVisible(false);
        if (aligned || attempts >= maxAttempts) return;
        attempts += 1;
        requestAnimationFrame(tryAlignSelected);
      };

      ensureVisibleTimerRef.current = setTimeout(() => {
        tryAlignSelected();
      }, 40);

      return () => {
        cancelled = true;
        if (ensureVisibleTimerRef.current) {
          clearTimeout(ensureVisibleTimerRef.current);
          ensureVisibleTimerRef.current = null;
        }
      };
    }, [isVisible, screenSize, selectedBlock, ensureSelectedIsVisible]);

    useEffect(() => {
      return () => {
        if (ensureVisibleTimerRef.current) {
          clearTimeout(ensureVisibleTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      if (!isVisible || screenSize === "small") return;
      updateRowScrollIndicators();
    }, [
      isVisible,
      screenSize,
      hoveredScrollButton,
      summaryBlocks.length,
      updateRowScrollIndicators,
    ]);

    const surfaceScale = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [0.97, 1],
    });
    const surfaceOpacity = progress.interpolate({
      inputRange: [0, 1],
      outputRange: [0.55, 1],
    });

    const handleClose = useCallback(() => {
      onClose?.();
    }, [onClose]);

    const handleSelectBlock = useCallback((block) => {
      if (!block?.id) return;
      setSelectedBlockId(block.id);
    }, []);

    const handleSummaryRowMouseEnter = useCallback(() => {
      if (isMobile || Platform.OS !== "web") return;
      setIsHoveringSummaryRow(true);
    }, [isMobile]);

    const handleSummaryRowMouseLeave = useCallback(() => {
      if (isMobile || Platform.OS !== "web") return;
      setIsHoveringSummaryRow(false);
      setHoveredScrollButton(null);
      rowEdgeHoverLockRef.current = { left: false, right: false };
      setRowEdgeHoverLock(rowEdgeHoverLockRef.current);
    }, [isMobile]);

    const showScrollControls = !isMobile && isHoveringSummaryRow;
    const shouldShowWideFade = isHoveringSummaryRow;
    const showLeftFade =
      rowScrollIndicators.canScrollLeft || rowEdgeHoverLock.left;
    const showRightFade =
      rowScrollIndicators.canScrollRight || rowEdgeHoverLock.right;

    if (!mounted) {
      return null;
    }

    return (
      <Modal
        transparent={true}
        visible={mounted}
        onRequestClose={handleClose}
        animationType="none"
      >
        <View style={styles.centeredView}>
          <Animated.View style={[styles.backdrop, { opacity: progress }]} />
          <TouchableOpacity
            style={styles.backdropPressable}
            activeOpacity={1}
            onPress={handleClose}
          />

          <Animated.View
            style={[
              styles.modalSurface,
              {
                opacity: surfaceOpacity,
                transform: [{ scale: surfaceScale }],
              },
            ]}
          >
            <View style={styles.header}>
              <Text style={styles.title} numberOfLines={2}>
                {t("summaryOf-string", { name: selectedBlock?.name ?? "" })}
              </Text>
              <TouchableOpacity
                style={[
                  styles.closeButton,
                  closeHovered && !isMobile ? styles.closeButtonHovered : null,
                ]}
                onPress={handleClose}
                onMouseEnter={() => !isMobile && setCloseHovered(true)}
                onMouseLeave={() => setCloseHovered(false)}
                activeOpacity={0.75}
              >
                <Ionicons name="close" size={22} color={Colors.darkgrayColor} />
              </TouchableOpacity>
            </View>

            <ScrollView
              style={styles.contentScroll}
              contentContainerStyle={styles.contentScrollBody}
              showsVerticalScrollIndicator={true}
            >
              <BlockSummaryOverviewContent block={selectedBlock} />
            </ScrollView>

            {screenSize !== "small" && summaryBlocks.length > 0 && (
              <View
                style={styles.bottomRowWrap}
                onMouseEnter={
                  !isMobile && Platform.OS === "web"
                    ? handleSummaryRowMouseEnter
                    : undefined
                }
                onMouseLeave={
                  !isMobile && Platform.OS === "web"
                    ? handleSummaryRowMouseLeave
                    : undefined
                }
              >
                <ScrollView
                  horizontal
                  ref={rowScrollRef}
                  showsHorizontalScrollIndicator={false}
                  onLayout={(event) => {
                    const width = event.nativeEvent.layout.width;
                    rowViewportWidthRef.current = width;
                    updateRowScrollIndicators(
                      rowScrollXRef.current,
                      width,
                      rowContentWidthRef.current,
                    );
                  }}
                  onScroll={(event) => {
                    updateRowScrollIndicators(
                      event.nativeEvent.contentOffset.x,
                      rowViewportWidthRef.current,
                      rowContentWidthRef.current,
                    );
                  }}
                  onContentSizeChange={(width) => {
                    rowContentWidthRef.current = width;
                    updateRowScrollIndicators(
                      rowScrollXRef.current,
                      rowViewportWidthRef.current,
                      width,
                    );
                  }}
                  scrollEventThrottle={16}
                  contentContainerStyle={styles.bottomRowContent}
                >
                  {summaryBlocks.map((block) => {
                    const isSelected = block?.id === selectedBlock?.id;
                    return (
                      <View
                        key={block.id}
                        onLayout={(event) => {
                          rowLayoutsRef.current[block.id] = {
                            x: event.nativeEvent.layout.x,
                            width: event.nativeEvent.layout.width,
                          };
                        }}
                        style={styles.rowCardWrapper}
                      >
                        <BlockCard
                          block={block}
                          folderKey="summary-modal"
                          blockId={block.id}
                          screenSize="small"
                          i18n={i18n}
                          t={t}
                          menuItems={[]}
                          onPress={handleSelectBlock}
                          onSummaryPress={() => {}}
                          onMenuAction={() => {}}
                          showRightIcons={false}
                          activeBlockId={activeBlockId}
                          isSelected={isSelected}
                          disableKeyboardFocus
                        />
                      </View>
                    );
                  })}
                </ScrollView>
                <View pointerEvents="box-none" style={styles.rowScrollOverlay}>
                  {showLeftFade && (
                    <LinearGradient
                      pointerEvents="none"
                      colors={
                        shouldShowWideFade
                          ? [
                              fadeColors.solid,
                              fadeColors.solid,
                              fadeColors.transparent,
                            ]
                          : [fadeColors.solid, fadeColors.transparent]
                      }
                      locations={shouldShowWideFade ? [0, 0.55, 1] : undefined}
                      start={{ x: 0, y: 0 }}
                      end={{ x: 1, y: 0 }}
                      style={[
                        styles.edgeFade,
                        styles.edgeFadeLeft,
                        shouldShowWideFade && styles.edgeFadeWide,
                      ]}
                    />
                  )}
                  {showRightFade && (
                    <LinearGradient
                      pointerEvents="none"
                      colors={
                        shouldShowWideFade
                          ? [
                              fadeColors.solid,
                              fadeColors.solid,
                              fadeColors.transparent,
                            ]
                          : [fadeColors.solid, fadeColors.transparent]
                      }
                      locations={shouldShowWideFade ? [0, 0.55, 1] : undefined}
                      start={{ x: 1, y: 0 }}
                      end={{ x: 0, y: 0 }}
                      style={[
                        styles.edgeFade,
                        styles.edgeFadeRight,
                        shouldShowWideFade && styles.edgeFadeWide,
                      ]}
                    />
                  )}
                  {rowEdgeHoverLock.left && (
                    <View
                      pointerEvents="auto"
                      style={[
                        styles.edgeBlocker,
                        styles.edgeBlockerLeft,
                        shouldShowWideFade && styles.edgeFadeWide,
                      ]}
                      onMouseLeave={
                        !isMobile
                          ? () => {
                              setHoveredScrollButton(null);
                              clearRowEdgeHoverLock("left");
                            }
                          : undefined
                      }
                    />
                  )}
                  {rowEdgeHoverLock.right && (
                    <View
                      pointerEvents="auto"
                      style={[
                        styles.edgeBlocker,
                        styles.edgeBlockerRight,
                        shouldShowWideFade && styles.edgeFadeWide,
                      ]}
                      onMouseLeave={
                        !isMobile
                          ? () => {
                              setHoveredScrollButton(null);
                              clearRowEdgeHoverLock("right");
                            }
                          : undefined
                      }
                    />
                  )}
                  {rowScrollIndicators.canScrollLeft && showScrollControls && (
                    <Tooltip
                      content="horizontalScrollHint-string"
                      placement="top"
                      delayedOpen
                    >
                      <HoverableTouchableOpacity
                        style={[styles.scrollButton, styles.scrollButtonLeft]}
                        onPress={() => nudgeRowScroll(-1)}
                        activeOpacity={0.8}
                        hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                        onMouseEnter={
                          !isMobile
                            ? () => setHoveredScrollButton("left")
                            : undefined
                        }
                        onMouseLeave={
                          !isMobile ? () => setHoveredScrollButton(null) : undefined
                        }
                      >
                        <Ionicons
                          name="chevron-back"
                          size={18}
                          color={
                            hoveredScrollButton === "left"
                              ? Colors.darkgrayColor
                              : Colors.grayColor
                          }
                        />
                      </HoverableTouchableOpacity>
                    </Tooltip>
                  )}
                  {rowScrollIndicators.canScrollRight && showScrollControls && (
                    <Tooltip
                      content="horizontalScrollHint-string"
                      placement="top"
                      delayedOpen
                    >
                      <HoverableTouchableOpacity
                        style={[styles.scrollButton, styles.scrollButtonRight]}
                        onPress={() => nudgeRowScroll(1)}
                        activeOpacity={0.8}
                        hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                        onMouseEnter={
                          !isMobile
                            ? () => setHoveredScrollButton("right")
                            : undefined
                        }
                        onMouseLeave={
                          !isMobile ? () => setHoveredScrollButton(null) : undefined
                        }
                      >
                        <Ionicons
                          name="chevron-forward"
                          size={18}
                          color={
                            hoveredScrollButton === "right"
                              ? Colors.darkgrayColor
                              : Colors.grayColor
                          }
                        />
                      </HoverableTouchableOpacity>
                    </Tooltip>
                  )}
                </View>
              </View>
            )}
          </Animated.View>
        </View>
      </Modal>
    );
  },
);

export default BlockSummaryModal;
